import React from 'react';

import Display from '@dashboard/components/Display';
import KeyChip from '@dashboard/components/KeyChip';
import Link from '@dashboard/components/mui-atoms/Link';
import TemplateActionsMenu from '@dashboard/components/TemplateActionsMenu';
import TemplateThumbnailPreview from '@dashboard/components/TemplateThumbnailPreview';
import { FWaypointTemplateFragment } from '@dashboard/generated/wp/platform';
import useCurrentHref from '@dashboard/hooks/navigate/useCurrentHref';
import useCurrentPlatformId from '@dashboard/hooks/route/useCurrentPlatformId';
import { MoreHorizOutlined, TableRowsOutlined } from '@mui/icons-material';
import { Card, CardActionArea, CardContent, CardMedia, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import { DateFormatter } from '@waypoint/react-formatters';

import TemplateSendsCount from './TemplateSendsCount';

type Props = {
  template: FWaypointTemplateFragment;
};

export default function TemplateSummaryCard({ template }: Props) {
  const backLinkHref = useCurrentHref();
  const platformId = useCurrentPlatformId();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const renderChip = () => {
    if (template.type === 'TEMPLATE') {
      return (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <KeyChip label={template.id} tooltipTitle="Copy template ID to clipboard" size="sm" />
        </Stack>
      );
    }
    return (
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Tooltip title="Type: Layout">
          <TableRowsOutlined fontSize="small" sx={{ color: 'grey.500' }} />
        </Tooltip>
        <Typography variant="body2" color="text.secondary">
          Layout
        </Typography>
      </Stack>
    );
  };

  return (
    <Card variant="outlined" sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 4', lg: 'span 3' } }}>
      <CardActionArea component={Link} href={`/o/${platformId}/templates/${template.id}`} backLinkHref={backLinkHref}>
        <CardMedia
          sx={{
            overflow: 'hidden',
            height: 196,
            backgroundPosition: 'top',
            border: '1px solid',
            borderColor: 'cadet.200',
          }}
        >
          <TemplateThumbnailPreview template={template} />
        </CardMedia>

        <CardContent sx={{ pt: 2.5 }}>
          <Typography fontWeight="medium" sx={{ mb: 0.5 }} noWrap>
            {template.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            Last edited: <DateFormatter value={template.lastPublishedAt || template.createdAt} template="condensed" />
            <Display display={template.type === 'TEMPLATE'}>
              <>
                &ensp;&middot;&ensp; <TemplateSendsCount id={template.id} />
              </>
            </Display>
          </Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1.25}>
            {renderChip()}
            <IconButton type="button" size="small" onClick={handleClick}>
              <MoreHorizOutlined fontSize="small" />
            </IconButton>
          </Stack>
        </CardContent>
      </CardActionArea>
      <TemplateActionsMenu anchorEl={anchorEl} setAnchorEl={setAnchorEl} templateId={template.id} />
    </Card>
  );
}
